import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuditoriaAdministracionApi } from '../../api/axios';
import GoBackArrow from '../General/GoBackArrow';
import BigSpinner from '../General/BigSpinner';
import SafeReactPaginate from '../General/SafeReactPaginate';
import '../General/AdminListCard.css';
import '../Publicaciones/Components/ABM/AuditoriaPrecioStock.css';

const DEFAULT_PAGE_SIZE = 10;
const ALLOWED_DAYS = ['30', '60', '90'];

const buildInitialFilters = () => ({
    categoria: '',
    dias: '30'
});

const toArray = (value) => (Array.isArray(value) ? value : []);

const toNumberOrUndefined = (value) => {
    if (value === '' || value == null) return undefined;
    const parsed = Number.parseInt(String(value).trim(), 10);
    return Number.isNaN(parsed) ? undefined : parsed;
};

const normalizeDaysFilter = (value) => (ALLOWED_DAYS.includes(String(value)) ? String(value) : '30');

const toStringOrUndefined = (value) => {
    const normalized = String(value ?? '').trim();
    return normalized ? normalized : undefined;
};

const normalizeDetalle = (item) => ({
    idMovimiento: item?.idMovimiento ?? item?.IdMovimiento ?? 0,
    idEntidad: item?.idEntidad ?? item?.IdEntidad ?? 0,
    fecha: item?.fecha ?? item?.Fecha ?? null,
    usuario: item?.usuario ?? item?.Usuario ?? '-',
    origen: item?.origen ?? item?.Origen ?? '-',
    accion: item?.accion ?? item?.Accion ?? '-',
    categoria: item?.categoria ?? item?.Categoria ?? '-',
    campo: item?.campo ?? item?.Campo ?? '-',
    datoAnterior: item?.datoAnterior ?? item?.DatoAnterior ?? '-',
    datoActual: item?.datoActual ?? item?.DatoActual ?? '-',
});

const normalizeResponse = (raw) => {
    const data = raw?.data ?? raw ?? {};
    const elementos = toArray(data?.detalles ?? data?.Detalles ?? data?.content ?? data?.Content).map(normalizeDetalle);
    const total = Number.parseInt(
        data?.cantidadMovimientos ?? data?.CantidadMovimientos ?? data?.total ?? data?.Total ?? elementos.length,
        10
    );

    const categoriasDisponibles = toArray(data?.categoriasDisponibles ?? data?.CategoriasDisponibles)
        .map((item) => String(item ?? '').trim())
        .filter(Boolean);

    return {
        elementos,
        cantidadMovimientos: Number.isNaN(total) ? elementos.length : total,
        fechaDesde: data?.fechaDesde ?? data?.FechaDesde ?? null,
        fechaHasta: data?.fechaHasta ?? data?.FechaHasta ?? null,
        dias: data?.dias ?? data?.Dias ?? null,
        categoriasDisponibles,
    };
};

const formatDateTime = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return new Intl.DateTimeFormat('es-AR', {
        dateStyle: 'short',
        timeStyle: 'short'
    }).format(date);
};

const buildRequest = ({ filters }) => {
    const request = {};

    const categoria = toStringOrUndefined(filters.categoria);
    const dias = toNumberOrUndefined(normalizeDaysFilter(filters.dias));

    if (categoria) request.Categoria = categoria;
    if (dias != null) request.Dias = dias;

    return request;
};

const getAccionPillClass = (accion) => {
    const normalized = String(accion ?? '').trim().toLowerCase();

    if (normalized === 'alta' || normalized === 'create') {
        return 'auditoria-publicacion__accion-pill auditoria-publicacion__accion-pill--alta';
    }
    if (normalized === 'edición' || normalized === 'edicion' || normalized === 'update') {
        return 'auditoria-publicacion__accion-pill auditoria-publicacion__accion-pill--edicion';
    }
    if (normalized === 'deshabilitación' || normalized === 'deshabilitacion' || normalized === 'delete') {
        return 'auditoria-publicacion__accion-pill auditoria-publicacion__accion-pill--baja';
    }

    return 'auditoria-publicacion__accion-pill';
};

function AuditoriaTipoPublicaciones() {
    const navigate = useNavigate();
    const [filters, setFilters] = useState(buildInitialFilters);
    const [appliedFilters, setAppliedFilters] = useState(buildInitialFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [response, setResponse] = useState(() => normalizeResponse({}));

    useEffect(() => {
        const fetchAuditoria = async () => {
            setLoading(true);
            setError('');

            try {
                const rawData = await getAuditoriaAdministracionApi(
                    buildRequest({ filters: appliedFilters })
                );

                setResponse(normalizeResponse(rawData));
            } catch (err) {
                setResponse(normalizeResponse({}));
                setError(err?.response?.data?.mensaje || 'No se pudo cargar la auditoría de administración.');
            } finally {
                setLoading(false);
            }
        };

        fetchAuditoria();
    }, [appliedFilters]);

    const filteredItems = useMemo(() => response.elementos, [response.elementos]);

    const totalPages = Math.max(1, Math.ceil(filteredItems.length / Math.max(1, pageSize)));

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const paginatedItems = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredItems.slice(start, start + pageSize);
    }, [currentPage, filteredItems, pageSize]);

    const summaryCards = useMemo(() => {
        const altas = filteredItems.filter((item) => String(item.accion).toLowerCase() === 'create').length;
        const actualizaciones = filteredItems.filter((item) => String(item.accion).toLowerCase() === 'update').length;
        const porFront = filteredItems.filter((item) => item.origen === 'Front').length;

        return [
            { label: 'Eventos totales', value: response.cantidadMovimientos || filteredItems.length },
            { label: 'En esta página', value: paginatedItems.length },
            { label: 'Altas', value: altas },
            { label: 'Actualizaciones', value: actualizaciones },
            { label: 'Origen Front', value: porFront, alert: porFront > 0 }
        ];
    }, [response.cantidadMovimientos, filteredItems, paginatedItems.length]);

    const rangoDesde = filteredItems.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const rangoHasta = filteredItems.length === 0
        ? 0
        : Math.min(currentPage * pageSize, filteredItems.length);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFilters((previous) => ({
            ...previous,
            [name]: value
        }));
    };

    const handleApplyFilters = (event) => {
        event.preventDefault();
        setCurrentPage(1);
        setAppliedFilters(filters);
    };

    const handleResetFilters = (event) => {
        event.preventDefault();
        const nextFilters = buildInitialFilters();
        setFilters(nextFilters);
        setAppliedFilters(nextFilters);
        setCurrentPage(1);
        setPageSize(DEFAULT_PAGE_SIZE);
    };

    const handlePageClick = (data) => {
        setCurrentPage(data.selected + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="container py-3 auditoria-modulo">
            <div className="row mb-3 mb-md-4 align-items-center py-3 px-2 rounded admin-page-heading auditoria-page-heading">
                <div className="col-auto">
                    <GoBackArrow
                        onBack={() => navigate('/Tipo/Publicaciones')}
                        label="Volver a tipos"
                    />
                </div>
                <div className="col">
                    <h4 className="mb-0 text-themecolor color-rojo-megatone fw-bold">
                        Auditoría de tipo de publicaciones
                    </h4>
                    <p className="small text-body-secondary mb-0 mt-1">
                        Seguimiento de movimientos de administración con filtros por categoría y período.
                    </p>
                </div>
            </div>

            <div className="auditoria-publicacion">
                <form onSubmit={handleApplyFilters} onReset={handleResetFilters}>
                    <div className="auditoria-publicacion__filters-card">
                        <div className="auditoria-publicacion__filters-header">
                            <h6 className="auditoria-publicacion__filters-title">
                                <i className="fa fa-filter" aria-hidden="true" />
                                Filtros de búsqueda
                            </h6>
                        </div>
                        <div className="auditoria-publicacion__filters-body">
                            <div className="row g-3 auditoria-publicacion__toolbar-grid">
                                <div className="col-12 col-md-5">
                                    <p className="auditoria-publicacion__filtro-label mb-0">Categoría</p>
                                    <select
                                        className="form-control"
                                        name="categoria"
                                        value={filters.categoria}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Todas</option>
                                        {response.categoriasDisponibles.map((categoria) => (
                                            <option key={categoria} value={categoria}>{categoria}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-12 col-md-3">
                                    <p className="auditoria-publicacion__filtro-label mb-0">Días</p>
                                    <select
                                        className="form-control"
                                        name="dias"
                                        value={normalizeDaysFilter(filters.dias)}
                                        onChange={handleInputChange}
                                    >
                                        <option value="30">30 días</option>
                                        <option value="60">60 días</option>
                                        <option value="90">90 días</option>
                                    </select>
                                </div>
                                <div className="col-12 col-md-2">
                                    <p className="auditoria-publicacion__filtro-label mb-0">Por página</p>
                                    <select
                                        className="form-control"
                                        value={pageSize}
                                        onChange={(event) => {
                                            setPageSize(Number.parseInt(event.target.value, 10));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="10">10</option>
                                        <option value="20">20</option>
                                        <option value="50">50</option>
                                    </select>
                                </div>
                            </div>
                            <div className="auditoria-publicacion__toolbar-actions">
                                <button
                                    type="submit"
                                    className="auditoria-publicacion__filter-btn auditoria-publicacion__filter-btn--primary"
                                    disabled={loading}
                                >
                                    {loading ? 'Buscando...' : 'Buscar'}
                                </button>
                                <button type="reset" className="auditoria-publicacion__filter-btn">
                                    Limpiar
                                </button>
                            </div>
                        </div>
                    </div>
                </form>

                <div className="auditoria-publicacion__metrics">
                    {summaryCards.map((card) => (
                        <div
                            key={card.label}
                            className={`auditoria-publicacion__card ${card.alert ? 'auditoria-publicacion__card--alert' : ''}`.trim()}
                        >
                            <strong>{card.value}</strong>
                            <span className="auditoria-publicacion__label">{card.label}</span>
                        </div>
                    ))}
                </div>

                {(response.fechaDesde || response.fechaHasta) && (
                    <div className="auditoria-publicacion__info">
                        <span>
                            Período consultado:{' '}
                            <strong>{formatDateTime(response.fechaDesde)}</strong>
                            {' — '}
                            <strong>{formatDateTime(response.fechaHasta)}</strong>
                        </span>
                        {filteredItems.length > 0 && (
                            <span>
                                Mostrando <strong>{rangoDesde}-{rangoHasta}</strong> de{' '}
                                <strong>{filteredItems.length}</strong> en pantalla
                            </span>
                        )}
                    </div>
                )}

                {error ? (
                    <div className="auditoria-publicacion__state auditoria-publicacion__state--error" role="alert">
                        {error}
                    </div>
                ) : null}

                {loading ? (
                    <BigSpinner message="Cargando auditoría de administración..." />
                ) : !error && paginatedItems.length === 0 ? (
                    <div className="auditoria-publicacion__state">
                        No se encontraron eventos para los filtros seleccionados.
                    </div>
                ) : !error ? (
                    <>
                        <div className="auditoria-publicacion__results-card">
                            <div className="auditoria-publicacion__results-header">
                                <h5 className="auditoria-publicacion__results-title mb-0">Eventos auditados</h5>
                                <span className="auditoria-publicacion__results-badge">
                                    {paginatedItems.length} en esta página
                                </span>
                            </div>
                            <div className="auditoria-publicacion__results-body">
                                <div className="table-responsive">
                                    <table className="table table-hover auditoria-publicacion__table mb-0">
                                        <thead>
                                            <tr>
                                                <th>Id movimiento</th>
                                                <th>Id entidad</th>
                                                <th>Acción</th>
                                                <th>Usuario</th>
                                                <th>Origen</th>
                                                <th>Categoría</th>
                                                <th>Campo</th>
                                                <th>Fecha</th>
                                                <th>Dato anterior</th>
                                                <th>Dato actual</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedItems.map((item) => (
                                                <tr key={`${item.idMovimiento}-${item.fecha}`}>
                                                    <td>
                                                        <div className="auditoria-publicacion__period">
                                                            <strong>{item.idMovimiento}</strong>
                                                        </div>
                                                    </td>
                                                    <td>{item.idEntidad}</td>
                                                    <td>
                                                        <span className={getAccionPillClass(item.accion)}>
                                                            {item.accion}
                                                        </span>
                                                    </td>
                                                    <td>{item.usuario}</td>
                                                    <td>{item.origen}</td>
                                                    <td>{item.categoria}</td>
                                                    <td>{item.campo}</td>
                                                    <td>{formatDateTime(item.fecha)}</td>
                                                    <td>{item.datoAnterior}</td>
                                                    <td>{item.datoActual}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {totalPages > 1 && (
                            <div className="auditoria-publicacion__pagination-wrap">
                                <SafeReactPaginate
                                    previousLabel="Anterior"
                                    nextLabel="Siguiente"
                                    breakLabel="..."
                                    breakClassName="break-me"
                                    pageCount={totalPages}
                                    marginPagesDisplayed={2}
                                    pageRangeDisplayed={5}
                                    onPageChange={handlePageClick}
                                    containerClassName="pagination auditoria-publicacion__pagination mb-0"
                                    subContainerClassName="pages pagination"
                                    activeClassName="active"
                                    forcePage={currentPage - 1}
                                    disableInitialCallback
                                />
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        </div>
    );
}

export default AuditoriaTipoPublicaciones;
